import { isTenantOwnerAdmin } from "utils/isTenantOwnerAdmin"
import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Input, Label, Row } from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "lib/formik"
import { useNavigate } from "react-router-dom"
import {
  addEmailsSchema,
  loyaltyPointInfoSchema,
} from "components/Common/validation"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"

import Spinners from "components/Shared/Spinners"

import { showAlert } from "store/reducers/alertReducer"
import {
  clearMenuCache,
  getForceUpdateData,
  getGlobalSettingByType,
  getPaymentMethodsScope,
  getDeliveryProfilesScope,
  updateGlobalSettingByType,
} from "store/actions/globalSettingAction"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import Switch from "components/Shared/Switch"
import ConfirmModal from "components/Common/ConfirmModal"
import {
  defaultPaymentMethodValues,
  PAYMENT_METHOD_TOGGLE_OPTIONS,
} from "./paymentMethodConfig"
import DeliveryProfileSettings from "./DeliveryProfileSettings"

const maintenanceModeValues = {
  iOSApp: false,
  androidApp: false,
  website: false,
}

const forceValues = {
  iOSApp: false,
  androidApp: false,
  iOSAppVersion: "",
  androidAppVersion: "",
}


const extractPaymentMethodToggles = (methods = {}) => {
  const next = { ...defaultPaymentMethodValues }
  PAYMENT_METHOD_TOGGLE_OPTIONS.forEach(({ key }) => {
    next[key] = Boolean(methods?.[key])
  })
  return next
}

const AppSettings = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("App Settings ")

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const {
    loading,
    isLoading,
    maintenanceMode,
    forceAppUpdate,
    paymentMethodsScope,
    erp,
  } = useSelector(state => state.GlobalSettings)

  const [formValues, setFormValues] = useState(maintenanceModeValues)
  const [forceAppUpdateValues, setForceAppUpdateValues] = useState(forceValues)
  const [brandPaymentMethodsById, setBrandPaymentMethodsById] = useState({})
  const [erpEnabled, setErpEnabled] = useState(false)

  const [actionType, setActionType] = useState(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pendingAction, setPendingAction] = useState(null)
  const [isOnlyLimitedAppSettingsAccess, setIsOnlyLimitedAppSettingsAccess] =
    useState(null)

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    edit: null,
    subPermissions: null,
  })
  const scopeCards = paymentMethodsScope?.cards || []
  const showBrandPaymentCards = scopeCards.length > 0

  const getAllowedMethodOptions = allowedMethods =>
    PAYMENT_METHOD_TOGGLE_OPTIONS.filter(option =>
      (allowedMethods || []).includes(option.key)
    )

  const renderPaymentMethodCard = ({
    title,
    cardType,
    scopeCard,
    paymentMethods,
    onToggle,
  }) => {
    if (!scopeCard) {
      return null
    }

    const allowedOptions = getAllowedMethodOptions(scopeCard.allowedMethods)
    if (allowedOptions.length === 0) {
      return null
    }

    return (
      <Col lg={6} key={cardType}>
        <Card className="full_height_card">
          <CardBody className="d-flex flex-column justify-content-between">
            <CardTitle>{title}</CardTitle>
            <Row>
              {allowedOptions.map(option => (
                <Col lg={12} key={option.key}>
                  <div className="d-flex justify-content-end align-items-center">
                    <Label
                      htmlFor={`${cardType}-${option.key}`}
                      className="col col-form-label"
                    >
                      {t(option.label)}
                    </Label>
                    <Switch
                      onClickHandler={() => onToggle(option.key)}
                      isChecked={paymentMethods[option.key]}
                      disabled={!usersPermissions.edit}
                    />
                  </div>
                  <hr />
                </Col>
              ))}
              {usersPermissions.edit && (
                <Col lg={12}>
                  <Button
                    color="success"
                    className="d-block ms-auto mt-4"
                    disabled={isLoading && actionType === cardType}
                    onClick={() => showBrandPaymentConfirmation(cardType)}
                  >
                    {t("Save")}
                  </Button>
                </Col>
              )}
            </Row>
          </CardBody>
        </Card>
      </Col>
    )
  }

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    if (!userRole || Array.isArray(userRole) || !userRole.permissions) {
      return
    }

    const isPermissionList = isPermissionEnabled(
      userRole,
      32,
      "app_settings_1",
      accountType
    )

    const isPermissionEdit = isPermissionEnabled(
      userRole,
      32,
      "app_settings_3",
      accountType
    )

    const isSubPermissionMenusEdit = getSubPermissions(
      userRole,
      4,
      "menus_3",
      accountType
    )
    const appSettingsViewSubs =
      getSubPermissions(userRole, 32, "app_settings_1", accountType) || {}
    const appSettingsEditSubs =
      getSubPermissions(userRole, 32, "app_settings_3", accountType) || {}

    const canClearMenuCache =
      isSubPermissionMenusEdit?.clear_menu_cache?.isEnabled === true
    const canViewErpIntegration =
      (isPermissionList &&
        appSettingsViewSubs.enable_erp_integration?.isEnabled === true) ||
      (isPermissionEdit &&
        appSettingsEditSubs.enable_erp_integration?.isEnabled === true)
    const canManageErpIntegration =
      isPermissionEdit &&
      appSettingsEditSubs.enable_erp_integration?.isEnabled === true

    // Keep in sync with sidebar + accessibleRoutes.hasAppSettingsAccess
    const hasPageAccess =
      isTenantOwnerAdmin() ||
      isPermissionList ||
      canClearMenuCache ||
      canViewErpIntegration

    if (!hasPageAccess) {
      navigate("/dashboard")
    } else {
      if (isPermissionList) {
        fetchMaintenanceSettings()
        fetchForceUpdateSettings()
        dispatch(getPaymentMethodsScope())
        dispatch(getDeliveryProfilesScope())
        if (canViewErpIntegration) {
          fetchErpSettings()
        }
      } else {
        setIsOnlyLimitedAppSettingsAccess(true)
        if (canViewErpIntegration) {
          fetchErpSettings()
        }
      }
    }
    setUsersPermissions({
      list: isPermissionList,
      edit: isPermissionEdit,
      subPermissions: isSubPermissionMenusEdit,
      canViewErpIntegration,
      canManageErpIntegration,
    })
  }, [userRole, accountType, navigate, dispatch])

  useEffect(() => {
    if (maintenanceMode && maintenanceMode?.maintenanceMode) {
      const { iOSApp, androidApp, website } = maintenanceMode?.maintenanceMode
      setFormValues({
        iOSApp,
        androidApp,
        website,
      })
    }
  }, [maintenanceMode])

  useEffect(() => {
    if (forceAppUpdate && forceAppUpdate?.forceAppUpdate) {
      const {
        iOSApp,
        androidApp,
        iOSAppVersion,
        androidAppVersion,
      } = forceAppUpdate?.forceAppUpdate
      setForceAppUpdateValues({
        iOSApp,
        androidApp,
        iOSAppVersion: iOSAppVersion || "",
        androidAppVersion: androidAppVersion || "",
      })
    }
  }, [forceAppUpdate])

  useEffect(() => {
    if (erp && erp?.erp) {
      const { enabled } = erp?.erp
      setErpEnabled(enabled ?? false)
    }
  }, [erp])

  useEffect(() => {
    if (!scopeCards.length) {
      return
    }

    setBrandPaymentMethodsById(prevValues => {
      const nextValues = { ...prevValues }
      scopeCards.forEach(scopeCard => {
        nextValues[scopeCard.brandId] = extractPaymentMethodToggles(
          scopeCard.tenantMethods
        )
      })
      return nextValues
    })
  }, [paymentMethodsScope, scopeCards.length])

  const fetchMaintenanceSettings = () => {
    const payload = {
      type: "maintenanceMode",
    }
    dispatch(getGlobalSettingByType(payload))
  }
  const fetchForceUpdateSettings = () => {
    const payload = {
      type: "forceAppUpdate",
    }
    dispatch(getForceUpdateData(payload))
  }
  const fetchErpSettings = () => {
    const payload = {
      type: "erp",
    }
    dispatch(getGlobalSettingByType(payload))
  }

  const handleMaintenanceModeChange = key => {
    setFormValues(prevValues => ({
      ...prevValues,
      [key]: !prevValues[key],
    }))
  }

  const handleForceUpdateChange = key => {
    setForceAppUpdateValues(prevValues => ({
      ...prevValues,
      [key]: !prevValues[key],
    }))
  }

  const handleForceUpdateVersionChange = (key, value) => {
    setForceAppUpdateValues(prevValues => ({
      ...prevValues,
      [key]: value,
    }))
  }

  const handleBrandPaymentMethodChange = (brandId, key) => {
    setBrandPaymentMethodsById(prevValues => ({
      ...prevValues,
      [brandId]: {
        ...(prevValues[brandId] || defaultPaymentMethodValues),
        [key]: !prevValues?.[brandId]?.[key],
      },
    }))
  }

  const handleErpChange = () => {
    setErpEnabled(prev => !prev)
  }

  const handleMaintenanceMode = () => {
    setActionType("maintenanceMode")
    const { iOSApp, androidApp, website } = formValues
    const payload = {
      id: maintenanceMode._id,
      type: "maintenanceMode",
      maintenanceMode: {
        iOSApp,
        androidApp,
        website,
      },
    }
    dispatch(updateGlobalSettingByType(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowConfirmModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
        setActionType(null)
      })
      .catch(error => {})
  }

  const handleForceUpdate = () => {
    setActionType("forceAppUpdate")
    const { iOSApp, androidApp, iOSAppVersion, androidAppVersion } =
      forceAppUpdateValues
    const payload = {
      id: forceAppUpdate._id,
      type: "forceAppUpdate",
      forceAppUpdate: {
        iOSApp,
        androidApp,
        iOSAppVersion: (iOSAppVersion || "").trim(),
        androidAppVersion: (androidAppVersion || "").trim(),
      },
    }
    dispatch(updateGlobalSettingByType(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowConfirmModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
        setActionType(null)
      })
      .catch(error => {})
  }
  const handleBrandPaymentMethodsSave = brandId => {
    const scopeCard = scopeCards.find(card => card.brandId === brandId)
    const paymentMethods =
      brandPaymentMethodsById[brandId] || defaultPaymentMethodValues
    const allowedKeys = scopeCard?.allowedMethods || []
    const areAllDisabled = allowedKeys.every(key => paymentMethods[key] === false)

    if (areAllDisabled) {
      dispatch(
        showAlert({
          text: t(
            "At least one payment method must be enabled before proceeding"
          ),
          type: "error",
        })
      )
      setShowConfirmModal(false)
      return
    }

    setActionType(brandId)
    const payload = {
      id: maintenanceMode._id,
      type: "brandPaymentMethods",
      brandId,
      paymentMethods: {
        COD: paymentMethods.COD,
        APPLE: paymentMethods.APPLE,
        CARD: paymentMethods.CARD,
        CARD_ON_DELIVERY: paymentMethods.CARD_ON_DELIVERY,
        MOKAFAA: paymentMethods.MOKAFAA,
        LOYALTY_POINTS: paymentMethods.LOYALTY_POINTS,
        FULL_DISCOUNT_OR_REWARDS: paymentMethods.FULL_DISCOUNT_OR_REWARDS,
        FULL_DISCOUNT: paymentMethods.FULL_DISCOUNT,
        WALLET: paymentMethods.WALLET,
        PAYTABS: paymentMethods.PAYTABS,
      },
    }
    dispatch(updateGlobalSettingByType(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowConfirmModal(false)
          dispatch(getPaymentMethodsScope())
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
        setActionType(null)
      })
      .catch(error => {})
  }

  const handleErpSave = () => {
    setActionType("erp")
    const payload = {
      id: erp?._id,
      type: "erp",
      erp: {
        enabled: erpEnabled,
      },
    }
    dispatch(updateGlobalSettingByType(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowConfirmModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
        setActionType(null)
      })
      .catch(error => {})
  }

  const handleClearMenuCache = () => {
    dispatch(clearMenuCache())
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowConfirmModal(false)
          setPendingAction(null)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const confirmAction = () => {
    if (pendingAction === "maintenanceMode") {
      handleMaintenanceMode()
    } else if (pendingAction === "forceAppUpdate") {
      handleForceUpdate()
    } else if (pendingAction?.startsWith("brandPaymentMethods:")) {
      handleBrandPaymentMethodsSave(pendingAction.replace("brandPaymentMethods:", ""))
    } else if (pendingAction === "erp") {
      handleErpSave()
    } else if (pendingAction === "clearMenuCache") {
      handleClearMenuCache()
    }
  }

  const cancelAction = () => {
    setShowConfirmModal(false)
    setPendingAction(null)
  }

  const showConfirmation = actionType => {
    setPendingAction(actionType)
    setShowConfirmModal(true)
  }

  const getConfirmMessage = () => {
    switch (pendingAction) {
      case "maintenanceMode":
        return t("Are you sure you want to save the maintenance mode settings?")
      case "forceAppUpdate":
        return t("Are you sure you want to save the force app update settings?")
      default:
        if (pendingAction?.startsWith("brandPaymentMethods:")) {
          const brandId = pendingAction.replace("brandPaymentMethods:", "")
          const scopeCard = scopeCards.find(card => card.brandId === brandId)
          return t(
            "Are you sure you want to save the payment methods for {{brand}}?",
            {
              brand: t(
                scopeCard?.displayName || scopeCard?.brandName || "Brand"
              ),
            }
          )
        }
        if (pendingAction === "erp") {
          return t("Are you sure you want to save the ERP settings?")
        }
        if (pendingAction === "clearMenuCache") {
          return t("Are you sure you want to clear the menu cache?")
        }
        return t("Are you sure you want to save these settings?")
    }
  }

  const showBrandPaymentConfirmation = brandId => {
    setPendingAction(`brandPaymentMethods:${brandId}`)
    setShowConfirmModal(true)
  }

  if (loading && !isOnlyLimitedAppSettingsAccess) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        {usersPermissions.list && (
          <>
            <Row>
              <Col lg={6}>
                <Card className="full_height_card">
                  <CardBody>
                    <CardTitle>{t("Maintenance Mode")}</CardTitle>
                    <span>
                      {t(
                        "This feature allows you to enable or disable maintenance mode for different platforms, including iOS App, Android App. When enabled, users will be notified that the platform is temporarily unavailable for updates or maintenance"
                      )}
                    </span>
                    <Row>
                      <Col lg={12}>
                        <hr className="mt-3" />
                        <div className="d-flex justify-content-end align-items-center">
                          <Label
                            htmlFor="backgroundEmails"
                            className="col col-form-label"
                          >
                            {t("iOS App")}
                          </Label>
                          <Switch
                            onClickHandler={() =>
                              handleMaintenanceModeChange("iOSApp")
                            }
                            isChecked={formValues.iOSApp}
                            disabled={!usersPermissions.edit}
                          />
                        </div>
                        <hr />
                      </Col>
                      <Col lg={12}>
                        <div className="d-flex justify-content-end align-items-center">
                          <Label
                            htmlFor="androidApp"
                            className="col col-form-label"
                          >
                            {t("Android App")}
                          </Label>
                          <Switch
                            onClickHandler={() =>
                              handleMaintenanceModeChange("androidApp")
                            }
                            isChecked={formValues.androidApp}
                            disabled={!usersPermissions.edit}
                          />
                        </div>
                        <hr />
                      </Col>
                      {/* <Col lg={12}>
                    <div className="d-flex justify-content-end align-items-center">
                      <Label htmlFor="website" className="col col-form-label">
                        {t("Website")}
                      </Label>
                      <Switch
                        onClickHandler={() =>
                          handleMaintenanceModeChange("website")
                        }
                        isChecked={formValues.website}
                        disabled={!usersPermissions.edit}
                      />
                    </div>
                    <hr />
                  </Col> */}
                      {usersPermissions.edit && (
                        <Col lg={12}>
                          <Button
                            color="success"
                            className="d-block ms-auto mt-4"
                            disabled={
                              isLoading && actionType === "maintenanceMode"
                            }
                            onClick={() => showConfirmation("maintenanceMode")}
                          >
                            {t("Save")}
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={6}>
                <Card className="full_height_card">
                  <CardBody>
                    <CardTitle>{t("Force App Update")}</CardTitle>
                    <span>
                      {t(
                        "This feature ensures that users update their app to the latest version. You can enable or disable forced updates for the iOS and Android platforms, helping maintain compatibility and security."
                      )}
                    </span>
                    <Row>
                      <Col lg={12}>
                        <hr className="mt-3" />
                        <div className="d-flex justify-content-end align-items-center">
                          <Label
                            htmlFor="forceUpdateIosApp"
                            className="col col-form-label"
                          >
                            {t("iOS App")}
                          </Label>
                          <Switch
                            onClickHandler={() =>
                              handleForceUpdateChange("iOSApp")
                            }
                            isChecked={forceAppUpdateValues.iOSApp}
                            disabled={!usersPermissions.edit}
                          />
                        </div>
                        <div className="mt-2 mb-2">
                          <Label
                            htmlFor="iOSAppVersion"
                            className="col-form-label"
                          >
                            {t("iOS Latest Version")}
                          </Label>
                          <Input
                            id="iOSAppVersion"
                            type="text"
                            placeholder={t("e.g. 1.2.3")}
                            value={forceAppUpdateValues.iOSAppVersion}
                            disabled={!usersPermissions.edit}
                            onChange={e =>
                              handleForceUpdateVersionChange(
                                "iOSAppVersion",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <hr />
                      </Col>
                      <Col lg={12}>
                        <div className="d-flex justify-content-end align-items-center">
                          <Label
                            htmlFor="forceUpdateAndroidApp"
                            className="col col-form-label"
                          >
                            {t("Android App")}
                          </Label>
                          <Switch
                            onClickHandler={() =>
                              handleForceUpdateChange("androidApp")
                            }
                            isChecked={forceAppUpdateValues.androidApp}
                            disabled={!usersPermissions.edit}
                          />
                        </div>
                        <div className="mt-2 mb-2">
                          <Label
                            htmlFor="androidAppVersion"
                            className="col-form-label"
                          >
                            {t("Android Latest Version")}
                          </Label>
                          <Input
                            id="androidAppVersion"
                            type="text"
                            placeholder={t("e.g. 1.2.3")}
                            value={forceAppUpdateValues.androidAppVersion}
                            disabled={!usersPermissions.edit}
                            onChange={e =>
                              handleForceUpdateVersionChange(
                                "androidAppVersion",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <hr />
                      </Col>
                      {usersPermissions.edit && (
                        <Col lg={12}>
                          <Button
                            color="success"
                            className="d-block ms-auto mt-4"
                            disabled={
                              isLoading && actionType === "forceAppUpdate"
                            }
                            onClick={() => showConfirmation("forceAppUpdate")}
                          >
                            {t("Save")}
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            {showBrandPaymentCards && (
              <DeliveryProfileSettings settingsId={maintenanceMode?._id} />
            )}
            {showBrandPaymentCards && (
              <Row>
                {scopeCards.map(scopeCard => {
                  const brandLabel =
                    scopeCard.displayName || scopeCard.brandName || ""
                  return renderPaymentMethodCard({
                    title: t(`Payment Methods for ${brandLabel}`, {
                      defaultValue: t("Payment Methods for {{brand}}", {
                        brand: t(brandLabel),
                      }),
                    }),
                    cardType: scopeCard.brandId,
                    scopeCard,
                    paymentMethods:
                      brandPaymentMethodsById[scopeCard.brandId] ||
                      defaultPaymentMethodValues,
                    onToggle: key =>
                      handleBrandPaymentMethodChange(scopeCard.brandId, key),
                  })
                })}
              </Row>
            )}
          </>
        )}
        <Row>
          {(usersPermissions?.subPermissions?.clear_menu_cache?.isEnabled ||
            isTenantOwnerAdmin()) && (
            <Col lg={6}>
              <Card className="full_height_card">
                <CardBody>
                  <CardTitle>{t("Clear Menu Cache")}</CardTitle>
                  <span>
                    {t(
                      "This feature allows you to clear the cached menu data for the application. It ensures that users can view the most recent updates to the menu, such as new items, changes in pricing, or any modifications made by the admin. This can be enabled or disabled for both iOS and Android platforms, providing a seamless and up-to-date experience for users."
                    )}
                  </span>

                  <Col lg={12}>
                    <Button
                      color="success"
                      className="d-block ms-auto mt-4"
                      disabled={isLoading}
                      onClick={() => showConfirmation("clearMenuCache")}
                    >
                      {t("Clear Cache")}
                    </Button>
                  </Col>
                </CardBody>
              </Card>
            </Col>
            )}
            {(usersPermissions?.canViewErpIntegration) && (
            <Col lg={6}>
              <Card className="full_height_card">
                <CardBody>
                  <CardTitle>{t("ERP Integration")}</CardTitle>
                  <span>
                    {t(
                      "When enabled, the system will send requests to the ERP APIs for synchronization. When disabled, ERP API calls will not be made."
                    )}
                  </span>
                  <Row>
                    <Col lg={12}>
                      <hr className="mt-3" />
                      <div className="d-flex justify-content-end align-items-center">
                        <Label
                          htmlFor="erpEnabled"
                          className="col col-form-label"
                        >
                          {t("Enable ERP")}
                        </Label>
                        <Switch
                          onClickHandler={handleErpChange}
                          isChecked={erpEnabled}
                          disabled={!usersPermissions?.canManageErpIntegration}
                        />
                      </div>
                      <hr />
                    </Col>
                    {usersPermissions?.canManageErpIntegration && (
                      <Col lg={12}>
                        <Button
                          color="success"
                          className="d-block ms-auto mt-4"
                          disabled={isLoading && actionType === "erp"}
                          onClick={() => showConfirmation("erp")}
                        >
                          {t("Save")}
                        </Button>
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            </Col>
            )}
        </Row>
      </div>
      <ConfirmModal
        show={showConfirmModal}
        onDeleteClick={confirmAction}
        onCloseClick={cancelAction}
        isLoading={
          isLoading &&
          (actionType === pendingAction || pendingAction === "clearMenuCache")
        }
        buttonText={t("Yes")}
        bodyTitle={getConfirmMessage()}
      />
    </div>
  )
}

export default AppSettings
